import { useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { BookOpen, History, Trash2, Users } from 'lucide-react';
import { useChildRecitations, useManagedBy, useMyChildren } from '@core/firebase/children';
import { useCoverage } from '@core/firebase/tracks';
import { deleteChildAccount } from '@core/firebase/voiceNotes';
import { useMyCurricula, useSchooling } from '@core/firebase/curricula';
import { curriculumProgress, unitProgress, unitAyahs } from '@core/quran/curriculumProgress';
import type { CurriculumUnit } from '@core/quran/curriculumProgress';
import { surahMeta } from '@core/quran/surahMeta';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ProgressBar } from '@/components/ProgressBar';
import { ErrorCurve } from '@/components/ErrorCurve';
import { VoiceNotes } from '@/components/VoiceNotes';
import { MistakeList } from '@/components/MistakeList';
import { ConfirmButton } from '@/components/ConfirmButton';
import {
  CardTitle, EmptyState, IconTile, Notice, PageHeader, SoftCard, StatTile, pill,
} from '@/components/PageKit';
import { cn } from '@/lib/cn';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * What a parent sees about ONE of their children.
 *
 * Everything here is read through the child's own uid, which the rules allow
 * only to that child's parent. The page does not check parentage itself and
 * must not appear to: the child list it is reached from is already filtered by
 * the same rule, and a second check in the UI would look like the real one.
 */
export default function ChildProgressPage() {
  const { childUid } = useParams();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const { t, language } = useLanguage();

  const { children, loading: listLoading } = useMyChildren(identity?.userId ?? null);
  const { managedBy } = useManagedBy(identity?.userId ?? null);
  const child = children.find((c) => c.uid === childUid) ?? null;

  const schooling = useSchooling(childUid ?? null);
  const { curricula } = useMyCurricula(schooling.country, schooling.grade);
  const coverage = useCoverage(childUid ?? null);
  const { recitations, loading: historyLoading } = useChildRecitations(childUid ?? null);
  const [deleteFailed, setDeleteFailed] = useState(false);

  /*
   * أ-04: "يُتاح طلب الحذف لوالده". A callable nobody can reach does not
   * satisfy that — the server side of this existed before any button did,
   * which meant a parent could not delete their child's account at all.
   *
   * Only the OWNER. A guardian who redeemed an invite reads, and deleting
   * somebody's child is not reading; the server refuses it either way.
   */
  const erase = async () => {
    if (!childUid) return;
    setDeleteFailed(false);
    try {
      await deleteChildAccount(childUid);
      navigate('/children', { replace: true });
    } catch {
      setDeleteFailed(true);
    }
  };

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'ar' ? meta.name : meta.englishName;
  };
  const place = (p: { surah: number; ayah: number } | null) =>
    (p ? `${surahName(p.surah)} ${count(p.ayah)}` : '—');
  const unitLabel = (unit: CurriculumUnit) => (
    unit.from === undefined
      ? t('curriculum.wholeSurah')
      : t('curriculum.ayahRange', { from: count(unit.from), to: count(unit.to as number) })
  );

  // A child holding the device is not a guardian: without this she reaches a
  // page about another account and is told "no such child".
  if (managedBy) return <Navigate to="/home" replace />;

  if (listLoading) {
    return <div data-testid="child-loading" className="card-soft mx-auto h-32 w-full max-w-6xl animate-pulse" />;
  }

  // Not in the parent's own list: either not theirs, or not a child at all.
  // The rules would refuse the reads anyway; this is what the reader sees.
  if (!child) {
    return (
      <SoftCard as="div" className="mx-auto w-full max-w-3xl">
        <EmptyState
          testId="no-such-child"
          icon={<Users />}
          title={t('children.emptyTitle')}
          action={(
            <Link to="/children" className={cn(pill.base, pill.md, pill.primary)}>
              {t('children.nav')}
            </Link>
          )}
        />
      </SoftCard>
    );
  }

  const totalErrors = recitations.reduce((sum, r) => sum + r.errors, 0);
  // The week first (board f2): it is what a parent opens this page to learn.
  const since = Date.now() - WEEK_MS;
  const week = recitations.filter((r) => r.endedAt >= since);
  const weekErrors = week.reduce((sum, r) => sum + r.errors, 0);
  const first = curricula[0];
  const syllabus = first ? curriculumProgress(coverage, first.units) : null;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      {/* One title: the child's name. */}
      <PageHeader
        title={child.displayName}
        subtitle={child.grade === null
          ? t('children.noGrade')
          : t('children.gradeLabel', { grade: count(child.grade) })}
        back={{ to: '/children', label: t('children.nav'), testId: 'back-to-children' }}
      />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-ink">{t('children.thisWeekTitle')}</h2>
        <div data-testid="week-stats" className="grid grid-cols-3 gap-4">
          <StatTile testId="week-recitations" value={count(week.length)} label={t('children.statRecitations')} />
          <StatTile testId="week-corrections" value={count(weekErrors)} label={t('children.statCorrections')} />
          <StatTile
            testId="week-syllabus"
            value={syllabus ? `${count(syllabus.percent)}%` : '—'}
            label={t('children.statSyllabus')}
          />
        </div>
      </section>

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          {/* Where the teacher corrected this child — every correction is
              written to `marks`, and until App Review's fifth refusal
              (2026-10-01) no screen showed one. */}
          <MistakeList studentId={childUid ?? null} />

          {/* م-3-04: what the teacher said, above the log of what happened —
              a guardian opens this page to hear it. */}
          <VoiceNotes studentId={childUid ?? null} />
        </div>

        <div className="space-y-6">
          {curricula.length === 0 ? (
            <SoftCard as="div">
              <EmptyState testId="no-syllabus" icon={<BookOpen />} title={t('children.noSyllabus')} />
            </SoftCard>
          ) : (
            curricula.map((c) => {
              const progress = curriculumProgress(coverage, c.units);
              return (
                <SoftCard key={c.id} testId={`curriculum-${c.id}`} className="space-y-4">
                  <CardTitle
                    icon={<BookOpen />}
                    trailing={(
                      <span data-testid={`percent-${c.id}`} className="shrink-0 text-sm text-helper">
                        {count(progress.covered)} / {count(progress.total)} · {count(progress.percent)}%
                      </span>
                    )}
                  >
                    {c.name}
                  </CardTitle>
                  <ProgressBar percent={progress.percent} label={c.name} />

                  {/* The curriculum's own order — never sorted here. */}
                  <ol className="space-y-3 pt-1">
                    {c.units.map((unit, i) => {
                      const one = unitProgress(coverage, unit);
                      return (
                        <li key={`${unit.surah}-${i}`} data-testid={`unit-${unit.surah}-${i}`} className="space-y-1.5">
                          <div className="flex items-center justify-between gap-2 text-sm">
                            <span className="text-ink">
                              {surahName(unit.surah)}
                              <span className="ms-2 text-xs text-helper">{unitLabel(unit)}</span>
                            </span>
                            <span className="text-helper">
                              {count(one.covered)} / {count(unitAyahs(unit))}
                            </span>
                          </div>
                          <ProgressBar percent={one.percent} label={surahName(unit.surah)} />
                        </li>
                      );
                    })}
                  </ol>
                </SoftCard>
              );
            })
          )}

          <SoftCard testId="history-card" className="space-y-4">
            <CardTitle
              icon={<History />}
              trailing={recitations.length > 0 ? (
                <span data-testid="total-errors" className="shrink-0 text-sm text-helper">
                  {t('children.totalErrors')}: {count(totalErrors)}
                </span>
              ) : undefined}
            >
              {t('children.history')}
            </CardTitle>

            {/* م-3-02: the curve answers "is she getting better?"; the list
                below answers "what happened, and when". */}
            {!historyLoading && recitations.length > 0 && <ErrorCurve turns={recitations} />}

            {historyLoading ? (
              <div data-testid="history-loading" className="h-16 animate-pulse rounded-[14px] bg-surface" />
            ) : recitations.length === 0 ? (
              <div data-testid="no-history" className="rounded-[18px] bg-surface px-4 py-5 text-center">
                <p className="font-semibold text-ink">{t('children.noHistory')}</p>
                <p className="mt-1 text-sm text-helper">{t('children.noHistoryBody')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-hair">
                {recitations.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`recitation-${r.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm"
                  >
                    <span className="text-ink">
                      {t('children.recitedFromTo', { from: place(r.from), to: place(r.to) })}
                    </span>
                    <span className={r.errors > 0 ? 'text-danger' : 'text-success'}>
                      {r.errors > 0
                        ? t('children.errorsIn', { count: r.errors, n: count(r.errors) })
                        : t('children.noErrors')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SoftCard>
        </div>
      </div>

      {/* أ-04. At the bottom, after everything it would take with it: red,
          cancellable, and the child's name typed before it acts (board f5). */}
      {child.relation === 'owner' && (
        <section className="card-soft flex flex-wrap items-start gap-4 p-5">
          <IconTile className="bg-danger/10 text-danger"><Trash2 /></IconTile>
          <div className="min-w-0 flex-1 space-y-1">
            <h2 className="text-lg font-semibold text-danger">
              {t('children.deleteNamed', { name: child.displayName })}
            </h2>
            <p data-testid="delete-account-why" className="text-sm text-helper">
              {t('children.deleteAccountWhy')}
            </p>
          </div>
          <ConfirmButton
            testId="delete-account"
            tone="danger"
            label={t('children.deleteAccount')}
            confirmLabel={t('children.deleteConfirm')}
            question={t('children.confirmDeleteAccount', { name: child.displayName })}
            typeToConfirm={{
              value: child.displayName,
              label: t('children.typeNameToConfirm', { name: child.displayName }),
            }}
            onConfirm={() => { void erase(); }}
            className="basis-full sm:basis-auto"
          />
          {deleteFailed && (
            <Notice tone="danger" testId="delete-account-error" className="basis-full">
              {t('children.deleteAccountFailed')}
            </Notice>
          )}
        </section>
      )}
    </div>
  );
}
